import type { NetMessage, PeerId } from './transport';
import type { HubMapId } from '../data/hub-map.config';
import type { DirectionEnum } from '../types/enums';

/**
 * 'active' is the only status used at this milestone. The others are reserved
 * for the richer presence layer (cinematic ghost label, in-dungeon ghost,
 * away) — see docs/multiplayer.md "Évolutions de la présence".
 */
export type PresenceStatus = 'active' | 'cinematic' | 'dungeon' | 'away';

export interface PresenceState {
  peerId: PeerId;
  x: number;
  y: number;
  direction: DirectionEnum;
  mapId: HubMapId;
  moving: boolean;
  status: PresenceStatus;
}

export interface HubPresenceMsg extends NetMessage {
  type: 'hub-presence';
  state: PresenceState;
}

export interface HubRosterMsg extends NetMessage {
  type: 'hub-roster';
  t: number;
  hostMapId: HubMapId;
  /** Authoritative player count (host's peers + itself) — single source of truth. */
  count: number;
  roster: PresenceState[];
}

export const HUB_PRESENCE_HZ = 15;
export const HUB_PRESENCE_INTERVAL_MS = 1000 / HUB_PRESENCE_HZ;

export function isHubRoster(m: NetMessage): m is HubRosterMsg {
  return m.type === 'hub-roster';
}
export function isHubPresence(m: NetMessage): m is HubPresenceMsg {
  return m.type === 'hub-presence';
}

const SMOOTH_PER_FRAME = 0.25;

/**
 * Renders a set of remote avatars by easing their on-screen position toward the
 * latest authoritative target. Non-authoritative presence is low-stakes, so a
 * frame-rate-aware exponential ease replaces the dungeon's time-buffer interp —
 * simpler and symmetric across host and client.
 */
export class PresenceView {
  private rendered = new Map<PeerId, PresenceState>();

  update(targets: PresenceState[], excludePeerId: PeerId, delta: number): void {
    const k = Math.min(1, SMOOTH_PER_FRAME * delta);
    const present = new Set<PeerId>();
    for (const t of targets) {
      if (t.peerId === excludePeerId) {
        continue;
      }
      present.add(t.peerId);
      const cur = this.rendered.get(t.peerId);
      if (!cur) {
        this.rendered.set(t.peerId, { ...t });
        continue;
      }
      cur.x += (t.x - cur.x) * k;
      cur.y += (t.y - cur.y) * k;
      cur.direction = t.direction;
      cur.moving = t.moving;
      cur.mapId = t.mapId;
      cur.status = t.status;
    }
    for (const id of [...this.rendered.keys()]) {
      if (!present.has(id)) {
        this.rendered.delete(id);
      }
    }
  }

  avatars(): PresenceState[] {
    return [...this.rendered.values()];
  }
}
